import { useEffect, useState } from "react";
import { toast } from "sonner";
import AttorneyShell from "@/components/AttorneyShell";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  KeyRound, Webhook, BookOpen, Plus, Trash2, Copy, Loader2, Check, ShieldCheck, Radio,
} from "lucide-react";

const SCOPES = ["cases:read", "evidence:read", "timeline:read", "messages:read", "webhooks:manage"];
const EVENTS = [
  "case.created", "case.claimed", "case.imported", "case.updated", "case.closed",
  "evidence.uploaded", "police_report.uploaded", "medical_record.uploaded",
  "message.sent", "document.requested", "document.uploaded", "timeline.updated",
  "payment.completed", "invoice.created", "attorney.connected",
  "crm.sync.completed", "crm.sync.failed",
];

function Reveal({ value, onDone }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(value); setCopied(true); toast.success("Copied"); };
  return (
    <div className="bg-navy rounded-2xl p-4 mt-3" data-testid="secret-reveal">
      <div className="text-xs text-slate-300 mb-1">Copy this now — it won't be shown again.</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-cobalt-light text-sm break-all">{value}</code>
        <button onClick={copy} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</button>
      </div>
      <button onClick={onDone} className="mt-3 text-xs text-slate-300 hover:text-white underline">Done</button>
    </div>
  );
}

function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState(["cases:read", "timeline:read"]);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const load = async () => { try { const { data } = await api.get("/attorney/api-keys"); setKeys(data); } catch { /* ignore */ } };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try { const { data } = await api.post("/attorney/api-keys", { name: name.trim(), scopes }); setNewKey(data.api_key); setName(""); load(); toast.success("API key created"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setCreating(false); }
  };
  const revoke = async (id) => { try { await api.delete(`/attorney/api-keys/${id}`); toast.success("Key revoked"); load(); } catch { toast.error("Failed"); } };
  const toggleScope = (s) => setScopes((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);

  return (
    <div className="space-y-6" data-testid="apikeys-tab">
      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-semibold text-navy mb-3">Create API Key</h3>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="apikey-name" placeholder="Key name (e.g. Firm CRM Sync)"
          className="w-full bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt outline-none" />
        <div className="mt-3 flex flex-wrap gap-2">
          {SCOPES.map((s) => (
            <button key={s} onClick={() => toggleScope(s)} data-testid={`scope-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${scopes.includes(s) ? "bg-cobalt text-white border-cobalt" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{s}</button>
          ))}
        </div>
        <button onClick={create} disabled={creating || !name.trim()} data-testid="create-apikey-btn"
          className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all active:scale-95 disabled:opacity-50">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Generate Key
        </button>
        {newKey && <Reveal value={newKey} onDone={() => setNewKey(null)} />}
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-semibold text-navy mb-3">Your API Keys</h3>
        {keys.length === 0 ? <p className="text-sm text-slate-400">No keys yet.</p> : (
          <div className="divide-y divide-slate-50">
            {keys.map((k) => (
              <div key={k.key_id} className="flex items-center justify-between gap-3 py-3" data-testid={`apikey-${k.key_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-navy">{k.name} {!k.active && <span className="text-xs text-red-500">(revoked)</span>}</div>
                  <div className="text-xs text-slate-400 font-mono">{k.masked_key} · {(k.scopes || []).join(", ")}</div>
                </div>
                {k.active && <button onClick={() => revoke(k.key_id)} data-testid={`revoke-${k.key_id}`} className="shrink-0 p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Webhooks() {
  const [hooks, setHooks] = useState([]);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState(["case.claimed", "message.sent"]);
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState(null);

  const load = async () => { try { const { data } = await api.get("/attorney/webhooks"); setHooks(data.webhooks); } catch { /* ignore */ } };
  useEffect(() => { load(); }, []);
  const toggle = (e) => setEvents((p) => p.includes(e) ? p.filter((x) => x !== e) : [...p, e]);

  const create = async () => {
    if (!url.trim()) return;
    setCreating(true);
    try { const { data } = await api.post("/attorney/webhooks", { url: url.trim(), events }); setSecret(data.signing_secret); setUrl(""); load(); toast.success("Webhook created"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setCreating(false); }
  };
  const del = async (id) => { try { await api.delete(`/attorney/webhooks/${id}`); toast.success("Webhook deleted"); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-6" data-testid="webhooks-tab">
      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-semibold text-navy mb-3">Add Webhook Endpoint</h3>
        <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="webhook-url" placeholder="https://your-system.com/webhooks/crashready"
          className="w-full bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt outline-none" />
        <div className="mt-3 flex flex-wrap gap-2 max-h-40 overflow-y-auto">
          {EVENTS.map((e) => (
            <button key={e} onClick={() => toggle(e)} data-testid={`event-${e}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${events.includes(e) ? "bg-navy text-white border-navy" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{e}</button>
          ))}
        </div>
        <button onClick={create} disabled={creating || !url.trim()} data-testid="create-webhook-btn"
          className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all active:scale-95 disabled:opacity-50">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />} Subscribe
        </button>
        {secret && <Reveal value={secret} onDone={() => setSecret(null)} />}
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-semibold text-navy mb-3">Active Endpoints</h3>
        {hooks.length === 0 ? <p className="text-sm text-slate-400">No webhooks yet.</p> : (
          <div className="divide-y divide-slate-50">
            {hooks.map((h) => (
              <div key={h.webhook_id} className="flex items-center justify-between gap-3 py-3" data-testid={`webhook-${h.webhook_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-navy truncate">{h.url}</div>
                  <div className="text-xs text-slate-400">{(h.events || []).length} events</div>
                </div>
                <button onClick={() => del(h.webhook_id)} data-testid={`delete-webhook-${h.webhook_id}`} className="shrink-0 p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Code = ({ children }) => <pre className="bg-navy text-cobalt-light text-xs rounded-2xl p-4 overflow-x-auto"><code>{children}</code></pre>;

function Docs() {
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
  return (
    <div className="space-y-6 max-w-3xl" data-testid="docs-tab">
      <section className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-bold text-navy text-lg">Authentication</h3>
        <p className="text-sm text-slate-600 mt-2">Crash Ready is the system of record for every case. Authenticate every request with your API key in the <code className="text-cobalt">X-API-Key</code> header. Keys are scoped and rate-limited (120 req/min). You can only access cases you have claimed.</p>
        <Code>{`curl ${base}/cases \\
  -H "X-API-Key: crk_xxxx_xxxxxxxx"`}</Code>
      </section>

      <section className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-bold text-navy text-lg">Endpoints (v1)</h3>
        <ul className="mt-2 text-sm text-slate-700 space-y-1.5">
          <li><code className="text-cobalt">GET /v1/cases</code> — list claimed cases <span className="text-slate-400">(cases:read)</span></li>
          <li><code className="text-cobalt">GET /v1/cases/{`{id}`}</code> — full normalized case <span className="text-slate-400">(cases:read)</span></li>
          <li><code className="text-cobalt">GET /v1/cases/{`{id}`}/timeline</code> — chronological events <span className="text-slate-400">(timeline:read)</span></li>
          <li><code className="text-cobalt">GET /v1/cases/{`{id}`}/evidence</code> — evidence + metadata <span className="text-slate-400">(evidence:read)</span></li>
          <li><code className="text-cobalt">GET /v1/cases/{`{id}`}/messages</code> — secure messages <span className="text-slate-400">(messages:read)</span></li>
        </ul>
        <h4 className="font-semibold text-navy text-sm mt-4">Example response</h4>
        <Code>{`{
  "case_id": "case_ab12...",
  "case_number": "CR-NC-2026-00018611",
  "system_of_record": "Crash Ready",
  "client": { "first_name": "...", "last_name": "...", "phone": "..." },
  "accident": { "state": "NC", "collision_type": "...", "police_report_number": "..." },
  "insurance": { "insurance_company": "...", "claim_number": "..." },
  "medical": { "reported_injuries": [...], "treatment": [...] },
  "evidence": [ { "evidence_id": "...", "file_type": "image/jpeg", "gps": {...} } ],
  "timeline": [ ... ],
  "notes": { "ai_summary": "..." }
}`}</Code>
      </section>

      <section className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-bold text-navy text-lg">Webhooks</h3>
        <p className="text-sm text-slate-600 mt-2">Subscribe to events; deliveries are HMAC-SHA256 signed in the <code className="text-cobalt">X-CrashReady-Signature</code> header (verify with your signing secret).</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {EVENTS.map((e) => <span key={e} className="text-[11px] font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600">{e}</span>)}
        </div>
        <Code>{`POST https://your-system.com/webhooks/crashready
X-CrashReady-Event: message.sent
X-CrashReady-Signature: sha256=<hmac>

{ "event": "message.sent", "data": { "case_id": "...", "message_id": "..." }, "sent_at": "..." }`}</Code>
      </section>

      <section className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-bold text-navy text-lg">Error Codes</h3>
        <ul className="mt-2 text-sm text-slate-700 space-y-1">
          <li><b>401</b> — missing/invalid API key</li>
          <li><b>403</b> — missing scope or not authorized for that case</li>
          <li><b>404</b> — case not found</li>
          <li><b>429</b> — rate limit exceeded (120/min)</li>
          <li><b>402</b> — access fee unpaid (import)</li>
        </ul>
      </section>
    </div>
  );
}

export default function Developer() {
  const [tab, setTab] = useState("keys");
  const TABS = [
    { key: "keys", label: "API Keys", icon: KeyRound },
    { key: "webhooks", label: "Webhooks", icon: Webhook },
    { key: "docs", label: "API Docs", icon: BookOpen },
  ];
  return (
    <AttorneyShell>
      <div data-testid="developer-page">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Developer Platform</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-1">Build on Crash Ready</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">Versioned, secure REST API and webhooks. Crash Ready is the authoritative source of truth; your systems synchronize with it.</p>

        <div className="mt-6 flex gap-2">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`dev-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${tab === t.key ? "bg-cobalt text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "keys" && <ApiKeys />}
          {tab === "webhooks" && <Webhooks />}
          {tab === "docs" && <Docs />}
        </div>
      </div>
    </AttorneyShell>
  );
}
